import { Component, type ErrorInfo, type ReactNode } from "react";

import { errorMessage } from "@/lib/errors";
import { Button, StatusPanel } from "@/ui";

type Props = {
  children: ReactNode;
  /** Changing this value resets the boundary — pass the current route path. */
  resetKey?: string;
};

type State = { error: Error | null };

/**
 * Stops one broken feature from taking down the whole window.
 *
 * The shell (navigation, connection status, sign out) stays usable, so the user
 * can move to another section instead of reloading.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Kobo] unhandled error in a feature", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <StatusPanel
        tone="error"
        icon={<span aria-hidden>!</span>}
        title="This section stopped working"
        description={errorMessage(error)}
        actions={
          <Button variant="primary" onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
        }
      />
    );
  }
}
